import { useEffect, useState } from 'react';
import { useToast } from '@powercore/ui';
import { Plus, Eye, EyeOff, Trash2, Save, X } from 'lucide-react';
import { api, type SkillCategory, type SkillGuide } from '../lib/api';

const font = { fontFamily: "'Outfit', sans-serif" };
const TOOLS = ['claude_code', 'chatgpt', 'codex', 'cursor', 'gemini'];

interface EditState {
  id?: string;
  slug: string;
  name: string;
  category_id: string;
  tagline: string;
  description: string;
  emoji: string;
  pricing_model: string;
  price_one_time_cents: string;
  featured: boolean;
  compatible_tools: string[];
  value_props: string; // newline separated
  skillmd: string;      // primary file content
  guides: SkillGuide[];
}

const empty = (categoryId = ''): EditState => ({
  slug: '', name: '', category_id: categoryId, tagline: '', description: '', emoji: '✨',
  pricing_model: 'subscription', price_one_time_cents: '', featured: false,
  compatible_tools: ['claude_code', 'chatgpt'], value_props: '', skillmd: '', guides: [],
});

export function AdminSkillsPage() {
  const { addToast } = useToast();
  const [skills, setSkills] = useState<any[]>([]);
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.adminListSkills().then((res) => {
      const data = (res.data as any)?.data || res.data;
      setSkills(data?.skills || []);
      setCategories(data?.categories || []);
    });
  };
  useEffect(load, []);

  const openNew = () => setEditing(empty(categories[0]?.id || ''));

  const openEdit = async (id: string) => {
    const res = await api.adminGetSkill(id);
    const s = (res.data as any)?.data || res.data;
    if (!s) return;
    const primary = (s.files || []).find((f: any) => f.is_primary) || (s.files || [])[0];
    setEditing({
      id: s.id, slug: s.slug, name: s.name, category_id: s.category_id || '',
      tagline: s.tagline || '', description: s.description || '', emoji: s.emoji || '✨',
      pricing_model: s.pricing_model || 'subscription',
      price_one_time_cents: s.price_one_time_cents ? String(s.price_one_time_cents) : '',
      featured: !!s.featured, compatible_tools: s.compatible_tools || [],
      value_props: (s.value_props || []).join('\n'),
      skillmd: primary?.content || '',
      guides: s.guides || [],
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.slug || !editing.name) {
      addToast({ title: 'Slug and name required', variant: 'error' });
      return;
    }
    setSaving(true);
    const fields = {
      slug: editing.slug, name: editing.name, category_id: editing.category_id || null,
      tagline: editing.tagline, description: editing.description, emoji: editing.emoji,
      pricing_model: editing.pricing_model,
      price_one_time_cents: editing.price_one_time_cents ? Number(editing.price_one_time_cents) : null,
      featured: editing.featured, compatible_tools: editing.compatible_tools,
      value_props: editing.value_props.split('\n').map((v) => v.trim()).filter(Boolean),
    };
    let id = editing.id;
    if (id) {
      await api.adminUpdateSkill(id, fields);
    } else {
      const res = await api.adminCreateSkill(fields);
      id = ((res.data as any)?.data || res.data)?.id;
    }
    if (id) {
      if (editing.skillmd.trim()) {
        await api.adminSetSkillFiles(id, [{ path: 'SKILL.md', content: editing.skillmd, is_primary: true }]);
      }
      if (editing.guides.length > 0) {
        await api.adminSetSkillGuides(id, editing.guides.filter((g) => g.steps?.trim()));
      }
    }
    setSaving(false);
    setEditing(null);
    addToast({ title: 'Saved', variant: 'success' });
    load();
  };

  const togglePublish = async (s: any) => {
    await api.adminPublishSkill(s.id, s.status === 'published');
    load();
  };

  const remove = async (s: any) => {
    if (!confirm(`Delete "${s.name}"? This removes its files and guides.`)) return;
    await api.adminDeleteSkill(s.id);
    load();
  };

  const setGuide = (tool: string, steps: string) => {
    if (!editing) return;
    const others = editing.guides.filter((g) => g.tool !== tool);
    setEditing({ ...editing, guides: steps.trim() ? [...others, { tool, steps }] : others });
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={font}>Curate Skills</h1>
          <p className="mt-1 text-sm text-gray-600" style={font}>{skills.length} skills · {categories.length} categories</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700" style={font}>
          <Plus className="h-4 w-4" /> New skill
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {skills.map((s) => (
          <div key={s.id} className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0">
            <span className="text-xl">{s.emoji || '✨'}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-gray-900" style={font}>{s.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{s.status}</span>
                {s.featured && <span className="rounded-full bg-mazo-50 px-2 py-0.5 text-[11px] font-medium text-mazo-700">featured</span>}
              </div>
              <div className="text-xs text-gray-400">{s.slug} · {s.install_count || 0} installs</div>
            </div>
            <button onClick={() => togglePublish(s)} className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50" title={s.status === 'published' ? 'Unpublish' : 'Publish'}>
              {s.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button onClick={() => openEdit(s.id)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50" style={font}>Edit</button>
            <button onClick={() => remove(s)} className="rounded-lg border border-gray-200 p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {skills.length === 0 && <div className="px-4 py-10 text-center text-sm text-gray-400" style={font}>No skills yet — create your first.</div>}
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-[9998] flex justify-end bg-black/40" onClick={() => setEditing(null)}>
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900" style={font}>{editing.id ? 'Edit skill' : 'New skill'}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name"><input className="ipt" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
                <Field label="Slug"><input className="ipt" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="my-skill" /></Field>
              </div>
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <Field label="Emoji"><input className="ipt" value={editing.emoji} onChange={(e) => setEditing({ ...editing, emoji: e.target.value })} /></Field>
                <Field label="Category">
                  <select className="ipt" value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}>
                    <option value="">— none —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Tagline"><input className="ipt" value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></Field>
              <Field label="Description"><textarea className="ipt h-20" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
              <Field label="Value props (one per line)"><textarea className="ipt h-24" value={editing.value_props} onChange={(e) => setEditing({ ...editing, value_props: e.target.value })} /></Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Pricing">
                  <select className="ipt" value={editing.pricing_model} onChange={(e) => setEditing({ ...editing, pricing_model: e.target.value })}>
                    <option value="subscription">Subscription</option>
                    <option value="one_time">One-time</option>
                    <option value="both">Both</option>
                    <option value="free">Free</option>
                  </select>
                </Field>
                <Field label="One-time price (cents)"><input className="ipt" value={editing.price_one_time_cents} onChange={(e) => setEditing({ ...editing, price_one_time_cents: e.target.value })} placeholder="4900" /></Field>
              </div>

              <Field label="Compatible tools">
                <div className="flex flex-wrap gap-2">
                  {TOOLS.map((t) => {
                    const on = editing.compatible_tools.includes(t);
                    return (
                      <button key={t} onClick={() => setEditing({ ...editing, compatible_tools: on ? editing.compatible_tools.filter((x) => x !== t) : [...editing.compatible_tools, t] })}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium ${on ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200'}`}>{t}</button>
                    );
                  })}
                </div>
              </Field>

              <label className="flex items-center gap-2 text-sm text-gray-700" style={font}>
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured on the storefront
              </label>

              <Field label="SKILL.md (primary file)"><textarea className="ipt h-48 font-mono text-xs" value={editing.skillmd} onChange={(e) => setEditing({ ...editing, skillmd: e.target.value })} placeholder="---&#10;name: my-skill&#10;description: ...&#10;---&#10;&#10;# My Skill" /></Field>

              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400" style={font}>Per-tool guides</div>
                {['claude_code', 'chatgpt', 'codex'].map((t) => (
                  <Field key={t} label={t}>
                    <textarea className="ipt h-20" value={editing.guides.find((g) => g.tool === t)?.steps || ''} onChange={(e) => setGuide(t, e.target.value)} placeholder={`How to use this in ${t}…`} />
                  </Field>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50" style={font}>Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700 disabled:opacity-60" style={font}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save skill'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.ipt{width:100%;border:1px solid #e5e7eb;border-radius:0.625rem;padding:0.5rem 0.75rem;font-size:0.875rem;color:#111827;outline:none;font-family:'Outfit',sans-serif}.ipt:focus{border-color:#a78bfa;box-shadow:0 0 0 2px #ede9fe}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-500" style={font}>{label}</span>
      {children}
    </label>
  );
}

export default AdminSkillsPage;
